import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO Component for Meta Tags, Open Graph, and JSON-LD Schema
 * Handles all meta tags for search engines and social media
 */
const SEOMeta = ({
  title = 'Algodoor - Programming & Cloud Training in Bhopal',
  description = 'Learn JavaScript, React, Node.js, AWS, and Full-Stack Development from industry experts. 100% Placement Support. Join thousands of successful students.',
  image = '/main-image.png',
  socialImage = null,
  socialTitle = null,
  socialDescription = null,
  url = 'https://www.algodoor.com',
  type = 'website',
  schemaMarkup = null,
  authors = 'Algodoor Team',
  noindex = false,
  keywords = '',
}) => {
  const baseUrl = 'https://www.algodoor.com';
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  const socialImageUrl = socialImage
    ? (socialImage.startsWith('http') ? socialImage : `${baseUrl}${socialImage}`)
    : imageUrl;
  const resolvedSocialTitle = socialTitle || 'Fullstack Javascript & AWS Training in Bhopal';
  const resolvedSocialDescription = socialDescription || 'Learn Fullstack JavaScript and AWS in Bhopal with practical projects, expert mentors, real interview preparation, and placement-focused training.';

  const robotsContent = noindex
    ? 'noindex, nofollow, noarchive, nosnippet'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  const googlebotContent = noindex ? 'noindex, nofollow' : 'index, follow';
  const normalizedType = (type || 'website').toLowerCase();

  return (
    <Helmet>
      {/* Essential Meta Tags */}
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta httpEquiv="X-UA-Compatible" content="ie=edge" />

      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="author" content={authors} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}

      {/* Robots and Crawlers */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={googlebotContent} />
      <meta name="revisit-after" content="7 days" />
      <meta name="language" content="English" />
      <meta name="distribution" content="global" />

      {/* Open Graph Meta Tags (Facebook, LinkedIn, WhatsApp) */}
      <meta property="og:type" content={normalizedType} />
      <meta property="og:title" content={resolvedSocialTitle} />
      <meta property="og:description" content={resolvedSocialDescription} />
      <meta property="og:image" content={socialImageUrl} />
      <meta property="og:image:url" content={socialImageUrl} />
      <meta property="og:image:secure_url" content={socialImageUrl} />
      <meta property="og:image:alt" content={resolvedSocialTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="Algodoor" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedSocialTitle} />
      <meta name="twitter:description" content={resolvedSocialDescription} />
      <meta name="twitter:image" content={socialImageUrl} />
      <meta name="twitter:image:src" content={socialImageUrl} />
      <meta name="twitter:image:alt" content={resolvedSocialTitle} />
      <meta name="twitter:creator" content="@algodoor" />
      <meta name="twitter:site" content="@algodoor" />

      {/* Additional Meta Tags for Better SEO */}
      <meta name="theme-color" content="#040C18" />
      <meta name="msapplication-TileColor" content="#040C18" />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* Preconnect to External Domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link rel="preconnect" href="https://www.google-analytics.com" />

      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />

      {/* Alternate Links for Multi-region */}
      <link rel="alternate" hrefLang="en-IN" href={fullUrl} />
      <link rel="alternate" hrefLang="en" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Schema.org JSON-LD */}
      {schemaMarkup && (
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOMeta;
